import { Http } from '../Utils/Http';
import { AxiosResponse } from 'axios';
import { IBcfAnalysisResponse, IBcfDefaults, IBcfImportPreview, IBcfImportResult, IBcfMappingConfig, IBcfImportSettings } from '../Store/BcfImport';

// Re-export interfaces for use in components
export type {
  IBcfAnalysisResponse,
  IBcfDefaults,
  IBcfImportPreview,
  IBcfImportResult,
  IBcfMappingConfig,
  IBcfImportSettings,
};

// API response wrapper from .NET
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  info: string | null;
}

// Also need to export the field mapping interface
export interface IBcfFieldMapping {
  fieldType: 'status' | 'type' | 'priority' | 'assignee' | 'stage' | 'labels';
  bcfValues: string[];
  mappingRules: Record<string, string | number>;
}

export class BcfImportApi {
  /**
   * Analyze uploaded BCF file
   * POST /api/bcf/analyze
   */
  static async analyzeBcfFile(file: File): Promise<AxiosResponse<ApiResponse<IBcfAnalysisResponse>>> {
    const formData = new FormData();
    formData.append('file', file);
    const axios = await Http.axios();
    return axios.post('/bcf/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  /**
   * Fetch default values for mappings
   * GET /api/bcf/defaults
   */
  static async fetchDefaults(constructionSiteId: string | number): Promise<AxiosResponse<ApiResponse<IBcfDefaults>>> {
    const axios = await Http.axios();
    return axios.get(`/bcf/defaults?constructionSiteId=${constructionSiteId}`);
  }

  /**
   * Validate mapping configuration
   * POST /api/bcf/validate-mapping
   */
  static async validateMapping(mappingConfig: IBcfMappingConfig): Promise<AxiosResponse<ApiResponse<{ isValid: boolean; errors?: string[] }>>> {
    const axios = await Http.axios();
    return axios.post('/bcf/validate-mapping', mappingConfig);
  }

  /**
   * Generate import preview
   * POST /api/bcf/preview
   */
  static async generatePreview(payload: {
    file: File;
    mappingConfigs: IBcfMappingConfig;
    settings: IBcfImportSettings;
  }): Promise<AxiosResponse<ApiResponse<IBcfImportPreview>>> {
    const formData = new FormData();
    formData.append('file', payload.file);
    formData.append('mappingConfigs', JSON.stringify(payload.mappingConfigs));
    formData.append('settings', JSON.stringify(payload.settings));

    const axios = await Http.axios();
    return axios.post('/bcf/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  /**
   * Execute import
   * POST /api/bcf/import
   * Expects JSON body matching BcfImportMappingDto
   */
  static async executeImport(payload: {
    fileId: string;
    mappingConfigs: IBcfMappingConfig;
    importSettings?: IBcfImportSettings;
    constructionSiteId: string | number;
  }): Promise<AxiosResponse<ApiResponse<IBcfImportResult>>> {
    // Build the mapping DTO expected by the backend
    // For ignored fields, send empty object and set ignore flags so backend knows to skip them
    const mappingDto = {
      FileId: payload.fileId,
      ConstructionSiteId: Number(payload.constructionSiteId),
      StatusMappings: payload.mappingConfigs.status?.mappingRules || {},
      TypeMappings: payload.mappingConfigs.type?.mappingRules || {},
      PriorityMappings: payload.mappingConfigs.priority?.isIgnored 
        ? {} 
        : (payload.mappingConfigs.priority?.mappingRules || {}),
      UserMappings: payload.mappingConfigs.user?.isIgnored 
        ? {} 
        : (payload.mappingConfigs.user?.mappingRules || {}),
      StageMappings: payload.mappingConfigs.stage?.isIgnored 
        ? {} 
        : (payload.mappingConfigs.stage?.mappingRules || {}),
      LabelMappings: payload.mappingConfigs.labels?.isIgnored 
        ? {} 
        : (payload.mappingConfigs.labels?.mappingRules || {}),
      DefaultDisciplineId: payload.importSettings?.defaultDisciplineId || null,
      DefaultPointName: payload.importSettings?.defaultPointName || null,
      UpdateExistingIssues: payload.importSettings?.updateExistingIssues ?? false,
      IgnorePriority: payload.mappingConfigs.priority?.isIgnored ?? false,
      IgnoreUser: payload.mappingConfigs.user?.isIgnored ?? false,
      IgnoreStage: payload.mappingConfigs.stage?.isIgnored ?? false,
      IgnoreLabels: payload.mappingConfigs.labels?.isIgnored ?? false,
    };

    const axios = await Http.axios();
    return axios.post('/bcf/import', mappingDto);
  }

  /**
   * Export activities to BCF format
   * POST /api/bcf/export
   * Returns 202 Accepted - export runs in background
   */
  static async exportBcf(payload: {
    constructionSiteId: string | number;
    version?: string;
    topicGuids?: string[];
  }): Promise<AxiosResponse<ApiResponse<{ queued: boolean }>>> {
    const axios = await Http.axios();
    const exportDto = {
      ConstructionSiteId: Number(payload.constructionSiteId),
      Version: payload.version || '3.0',
      TopicGuids: payload.topicGuids || null,
    };
    return axios.post('/bcf/export', exportDto);
  }

  /**
   * Download exported BCF file
   * GET /api/bcf/DownloadExport
   * Returns file blob for download
   */
  static async downloadExport(fileId: string): Promise<AxiosResponse<Blob>> {
    const axios = await Http.axios();
    return axios.get('/bcf/DownloadExport', {
      params: { fileId },
      responseType: 'blob',
    });
  }
}
